import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Folder, MessageSquare, Calendar, Wallet, FileText, Download, Eye } from 'lucide-react';
import { useLanguage } from '../../i18n/index.jsx';
import contactService from '../../services/contactService';
import { documentService } from '../../services/documentService';
import { isPastEvent } from '../../utils/eventTiming';
import ContactEventDetail from './ContactEventDetail.jsx';
import { userErrorMessage } from '../../utils/userError';

// ── Shared month-grouping helpers (Interaction Bar drawers) ────────────
const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

const tsOf = (row) => {
    const candidates = [row?.start_time, row?.created_at, row?.updated_at, row?.expense_date];
    for (const c of candidates) {
        if (!c) continue;
        const t = Date.parse(c);
        if (!Number.isNaN(t)) return t;
    }
    return 0;
};

/** Group rows by Month + Year of their best-available timestamp, newest month
 *  first; items inside each bucket newest-first. Rows without any parseable
 *  timestamp fall into a trailing "—" bucket so they are never dropped. */
export const groupByMonth = (rows) => {
    const buckets = new Map();
    for (const r of rows || []) {
        const ts = tsOf(r);
        let key;
        let label;
        let sortTs;
        if (ts) {
            const d = new Date(ts);
            key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            label = `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
            sortTs = d.getTime();
        } else {
            key = '0000-00';
            label = '—';
            sortTs = -1;
        }
        if (!buckets.has(key)) buckets.set(key, { key, label, ts: sortTs, rows: [] });
        buckets.get(key).rows.push({ ...r, __ts: ts });
    }
    const out = Array.from(buckets.values());
    out.sort((a, b) => b.ts - a.ts);
    out.forEach((g) => g.rows.sort((a, b) => (b.__ts || 0) - (a.__ts || 0)));
    return out;
};

// A drawer that lists asset NAMES only, grouped by [Month Year]. No direction
// dots / arrows / labels (FE-5 "No Clutter"). `renderRow(row)` draws one entry.
//
// The count lives HERE, not on the navigation icon (Update contacts features
// #307): a "Total: N" label opens the section's content area, directly above the
// most recent chronological grouping.
//
// The total is `rows.length` — the length of the very array mapped below it — and
// there is deliberately NO override prop ("Total Events shared"). `groupByMonth`
// buckets every row (undated ones land in the trailing "—" group rather than
// being dropped), so "Total == rows rendered" holds by construction instead of
// depending on a second computation staying in step with the first. The Events
// section used to pass an override excluding already-passed events (#305); the PO
// reversed that, and removing the seam is what stops it drifting back.
const GroupedDrawer = ({ rows, emptyLabel, renderRow, totalTestId, totalLabel }) => {
    const groups = useMemo(() => groupByMonth(rows), [rows]);
    if (!rows || rows.length === 0) {
        return <p className="text-xs text-pai-meta italic px-1 py-1">{emptyLabel}</p>;
    }
    return (
        <div className="space-y-3">
            <div
                data-testid={totalTestId}
                className="text-[11px] font-semibold text-pai-secondary tnum px-1"
            >
                {`${totalLabel}: ${rows.length}`}
            </div>
            {groups.map((g) => (
                <div key={g.key}>
                    <div className="text-[10px] uppercase tracking-wide font-semibold text-pai-meta mb-1">
                        {g.label}
                    </div>
                    <div className="space-y-0.5">
                        {g.rows.map((row) => renderRow(row))}
                    </div>
                </div>
            ))}
        </div>
    );
};

// A single doc row: name → Preview, plus Download.
const DocRow = ({ doc, onPreview }) => {
    const { t } = useLanguage();
    const handleDownload = async (e) => {
        e?.stopPropagation();
        try { await documentService.downloadDocument(doc.id, doc.filename); }
        catch (_) { /* download errors surface via the browser */ }
    };
    return (
        <div className="group flex items-center gap-1.5">
            <FileText size={11} className="text-pai-faint flex-shrink-0" />
            <button
                onClick={() => onPreview?.(doc)}
                className="flex-1 min-w-0 text-left text-xs text-pai-secondary hover:underline truncate"
                title={doc.filename}
            >
                {doc.filename}
            </button>
            <button
                onClick={() => onPreview?.(doc)}
                className="opacity-0 group-hover:opacity-100 pai-touch-reveal p-1 text-pai-faint hover:text-white transition-opacity"
                title={t('commonPreview', 'Preview')}
            >
                <Eye size={11} />
            </button>
            <button
                onClick={handleDownload}
                className="opacity-0 group-hover:opacity-100 pai-touch-reveal p-1 text-pai-faint hover:text-white transition-opacity"
                title={t('commonDownload', 'Download')}
            >
                <Download size={11} />
            </button>
        </div>
    );
};

const NameRow = ({ label, onClick }) => (
    <button
        onClick={onClick}
        className="w-full text-left text-xs text-pai-secondary hover:underline truncate block"
        title={label}
    >
        {label}
    </button>
);

// ── PillarBar — the reusable 4-pillar icon bar + monthly-grouped drawers ────
// Parametrized so BOTH the contact Interaction Bar (network contacts) and the
// team pillars (Phase C) render the exact same "icons-only, no clutter" drawer
// pattern (FE-5) without duplicating it. The caller supplies the already-loaded
// asset arrays + the testids (so the two consumers keep distinct testids). All
// four toggles are independent (opening one never closes another).
//
//   testIds: { bar, docs, chats, events, financials,
//              drawerDocs, drawerChats, drawerEvents, drawerFinancials }
export const PillarBar = ({
    testIds = {}, docs = [], convs = [], events = [], books = [],
    loading = false, error = null, navigate, onPreviewDoc,
    emptyDocs, emptyChats, emptyEvents, emptyFinancials,
    // Events-pillar override (#305). The contact Interaction Bar renders a
    // richer, clickable row; the Teams pillars pass none and keep the plain
    // name-row behaviour. There is no total override — see GroupedDrawer.
    renderEventRow,
}) => {
    const { t } = useLanguage();
    const [open, setOpen] = useState({ docs: false, chats: false, events: false, financials: false });
    const toggle = (key) => setOpen((s) => ({ ...s, [key]: !s[key] }));

    // Container-driven label collapse (#307, the #142/#231 precedent). This bar
    // lives in a DOCKED SPLIT PANEL that can be narrow while the viewport is
    // wide, so a viewport `md:` breakpoint is the wrong signal — a ResizeObserver
    // on the bar itself decides whether the four word labels fit beside their
    // icons. Below the threshold the labels drop and the icons stand alone.
    const barRef = useRef(null);
    const [compact, setCompact] = useState(false);
    useEffect(() => {
        const el = barRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const w = entry.contentRect?.width ?? el.clientWidth;
                setCompact(w < 440);
            }
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const totalLabel = t('pillarTotal', 'Total');

    const PILLARS = [
        { key: 'docs', testid: testIds.docs, Icon: Folder, label: t('pillarLabelDocs', 'Files') },
        { key: 'chats', testid: testIds.chats, Icon: MessageSquare, label: t('pillarLabelChats', 'Conversations') },
        { key: 'events', testid: testIds.events, Icon: Calendar, label: t('pillarLabelEvents', 'Events') },
        // A universal ledger/wallet mark, deliberately NOT a currency symbol —
        // the module is multi-currency and "$" reads as one locale's money (#307).
        { key: 'financials', testid: testIds.financials, Icon: Wallet, label: t('pillarLabelFinancials', 'Finance') },
    ];

    return (
        <section data-testid={testIds.bar} className="bg-white/[0.03] rounded-row border border-pai-subtle p-3 space-y-2">
            {/* Icon + word navigation. NO count bubbles: the total belongs to the
                section's content (see GroupedDrawer), not to its nav icon. */}
            <div ref={barRef} data-compact={compact ? 'true' : 'false'} className="flex items-center gap-1">
                {PILLARS.map(({ key, testid, Icon, label }) => (
                    <button
                        key={key}
                        data-testid={testid}
                        onClick={() => toggle(key)}
                        aria-pressed={open[key]}
                        aria-label={label}
                        title={label}
                        className={`flex-1 min-w-0 flex items-center justify-center gap-1.5 py-2 px-1 rounded-row border transition-colors ${
                            open[key]
                                ? 'border-white/80 bg-white/[0.055] text-white shadow-glow-sm'
                                : 'border-pai-subtle text-pai-meta hover:bg-white/[0.05]'
                        }`}
                    >
                        {/* The icon keeps its OWN lucide class (`lucide-wallet`,
                            `lucide-folder`, …) — the shrink guard rides a wrapper
                            because a `className` prop replaces lucide's, which
                            would erase the only handle on the icon's identity. */}
                        <span className="flex-shrink-0 flex items-center"><Icon size={16} /></span>
                        {!compact && (
                            <span data-testid={`${testid}-label`} className="text-xs font-medium truncate">
                                {label}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {loading && <div className="text-[10px] text-pai-faint px-1">{t('auxLoadingEllipsis', 'Loading…')}</div>}
            {error && (
                <div className="bg-pai-red/[0.08] border border-pai-red/40 rounded-ctl p-2 text-xs text-pai-red">
                    {error}
                </div>
            )}

            {open.docs && (
                <div data-testid={testIds.drawerDocs} className="pt-1 border-t border-pai-hairline">
                    <GroupedDrawer
                        rows={docs}
                        emptyLabel={emptyDocs || t('pillarDocsEmpty')}
                        totalLabel={totalLabel}
                        totalTestId={`${testIds.drawerDocs}-total`}
                        renderRow={(d) => <DocRow key={d.id} doc={d} onPreview={onPreviewDoc} />}
                    />
                </div>
            )}
            {open.chats && (
                <div data-testid={testIds.drawerChats} className="pt-1 border-t border-pai-hairline">
                    <GroupedDrawer
                        rows={convs}
                        emptyLabel={emptyChats || t('pillarChatsEmpty')}
                        totalLabel={totalLabel}
                        totalTestId={`${testIds.drawerChats}-total`}
                        renderRow={(c) => (
                            <NameRow key={c.id} label={c.title || 'Untitled conversation'} onClick={() => c?.id && navigate?.(`/chat/${c.id}`)} />
                        )}
                    />
                </div>
            )}
            {open.events && (
                <div data-testid={testIds.drawerEvents} className="pt-1 border-t border-pai-hairline">
                    <GroupedDrawer
                        rows={events}
                        emptyLabel={emptyEvents || t('pillarEventsEmpty')}
                        totalLabel={totalLabel}
                        totalTestId={`${testIds.drawerEvents}-total`}
                        renderRow={renderEventRow || ((e) => (
                            <NameRow key={e.id} label={e.title || 'Untitled event'} onClick={() => e?.id && navigate?.(`/calendar?focus_event=${e.id}`)} />
                        ))}
                    />
                </div>
            )}
            {open.financials && (
                <div data-testid={testIds.drawerFinancials} className="pt-1 border-t border-pai-hairline">
                    <GroupedDrawer
                        rows={books}
                        emptyLabel={emptyFinancials || t('pillarFinancialsEmpty')}
                        totalLabel={totalLabel}
                        totalTestId={`${testIds.drawerFinancials}-total`}
                        renderRow={(b) => (
                            <NameRow key={b.id} label={b.name || 'Untitled book'} onClick={() => b?.id && navigate?.(`/expenses?book=${b.id}`)} />
                        )}
                    />
                </div>
            )}
        </section>
    );
};

// ── Interaction Bar — 4 icon pillars (Network contact rows only) ───────────
// The caller only renders this for LINKED (network) contacts, so the omnis_user_id
// guard here is a defensive no-op. Delegates the icon-bar/drawer rendering to the
// shared PillarBar; only the fetch (getInteractions) lives here.
const InteractionBar = ({ contact, navigate, onPreviewDoc }) => {
    const { t } = useLanguage();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [openEvent, setOpenEvent] = useState(null);

    useEffect(() => {
        let cancelled = false;
        if (!contact?.id) return undefined;
        setLoading(true);
        setError(null);
        contactService
            .getInteractions(contact.id)
            .then((res) => { if (!cancelled) setData(res); })
            .catch((err) => { if (!cancelled) setError(userErrorMessage(err)); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [contact?.id, contact?.omnis_user_id]);

    const events = useMemo(() => data?.shared_events || [], [data]);
    // Past events are LISTED, COUNTED, and rendered de-emphasized. The timeline
    // is a history, so the total is every logged event ("Total Events shared" —
    // reversing #305's upcoming-only count); `isPastEvent` now only drives the
    // greying + the `data-past` stamp, never the number.

    const renderEventRow = (e) => {
        const past = isPastEvent(e);
        return (
            <button
                key={e.id}
                data-testid="contact-event-row"
                data-past={past ? 'true' : 'false'}
                data-organizer={e.is_organizer ? 'true' : 'false'}
                onClick={() => setOpenEvent(e)}
                title={e.title || ''}
                className={`w-full text-left text-xs hover:underline truncate block ${
                    past ? 'text-pai-faint' : 'text-pai-secondary'
                }`}
            >
                {e.title || t('contactEventUntitled', 'Untitled event')}
            </button>
        );
    };

    return (
        <>
        {openEvent && (
            <ContactEventDetail
                event={openEvent}
                contactName={contact?.name}
                onClose={() => setOpenEvent(null)}
                // Editing is the CALENDAR's job — hand off to its own edit
                // surface rather than duplicating an event form in here.
                onEdit={(ev) => { setOpenEvent(null); navigate?.(`/calendar?focus_event=${ev.id}&edit=1`); }}
            />
        )}
        <PillarBar
            testIds={{
                bar: 'interaction-bar',
                docs: 'pillar-docs', chats: 'pillar-chats', events: 'pillar-events', financials: 'pillar-financials',
                drawerDocs: 'pillar-drawer-docs', drawerChats: 'pillar-drawer-chats',
                drawerEvents: 'pillar-drawer-events', drawerFinancials: 'pillar-drawer-financials',
            }}
            docs={data?.shared_documents || []}
            convs={data?.shared_conversations || []}
            events={events}
            books={data?.shared_books || []}
            renderEventRow={renderEventRow}
            loading={loading}
            error={error}
            navigate={navigate}
            onPreviewDoc={onPreviewDoc}
        />
        </>
    );
};

export default InteractionBar;
